import React, { useState } from 'react';

import {
  DeleteOutlined,
  EditOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Input, Popconfirm, Tooltip } from 'antd';
import { VERTICAL_NAVIGATION_HEIGHT } from 'App/frames/Navigation/navConstants';
import { colors } from 'theme/colors';

import { Button } from 'components/Button';

import { capitalize, formatHumanDate } from 'utils/formatting';

import { ChatStarters } from './ChatStarters';
import { ChatConversation } from './shared';
import { TokenUsageBox } from './TokenUsageBox';
import { useStreamingChat } from './useStreamingChat';

// A disabled button fires no mouse events, so a Tooltip wrapped around one never opens.
// Applied to the wrapper, this lets hover land on the wrapper instead of the button.
const disabledTooltipTargetStyle = css`
  cursor: not-allowed;

  .ant-btn {
    pointer-events: none;
  }
`;

// Human-readable relative day + locale time, e.g. "Today 2:14 PM", "Yesterday 9:03 AM".
function formatConversationTimestamp(iso: string): string {
  const day = capitalize(formatHumanDate(iso));
  const time = new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${day} ${time}`;
}

function ChatPageHeader() {
  return (
    <div
      className={css`
        padding: 20px 24px 12px;
      `}
    >
      <div
        className={css`
          display: flex;
          align-items: center;
          gap: 10px;
        `}
      >
        <span
          className={css`
            font-size: 28px;
            font-weight: 700;
            color: ${colors.neutral[900]};
            line-height: 1.1;
          `}
        >
          Cue
        </span>
        <Tooltip title="Ask questions about your business data and get answers powered by AI.">
          <QuestionCircleOutlined style={{ color: colors.neutral[400], fontSize: 18 }} />
        </Tooltip>
      </div>
      <div
        className={css`
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 6px;
          font-size: 14px;
          color: ${colors.success[600]};
        `}
      >
        <span
          className={css`
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: ${colors.success[500]};
          `}
        />
        Available
      </div>
    </div>
  );
}

export function ChatWithDataPage() {
  const {
    messages,
    isLoading,
    sendMessage,
    conversations,
    activeConversationId,
    isAtConversationLimit,
    maxConversations,
    selectConversation,
    newConversation,
    deleteConversation,
    renameConversation,
  } = useStreamingChat();

  const newChatTooltip = isAtConversationLimit
    ? `You've reached the limit of ${maxConversations} saved conversations. Delete some past conversations to start a new one.`
    : 'New chat';
  const newChatDisabled = isAtConversationLimit || messages.length === 0;

  // Inline rename state for the conversation list.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const startRename = (id: string, title: string) => {
    setEditingId(id);
    setEditingTitle(title);
  };

  const commitRename = (id: string) => {
    if (editingId !== id) return; // guards double-commit (onPressEnter then onBlur)
    renameConversation(id, editingTitle);
    setEditingId(null);
  };

  return (
    <div
      className={css`
        display: flex;
        height: calc(100vh - ${VERTICAL_NAVIGATION_HEIGHT + 20}px);
        border: 1px solid ${colors.neutral[300]};
        border-radius: 8px;
        overflow: hidden;
        background: ${colors.neutral[0]};
      `}
    >
      {sidebarCollapsed ? (
        <div
          className={css`
            width: 44px;
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            padding: 12px 0;
            border-right: 1px solid ${colors.neutral[200]};
          `}
        >
          <Tooltip title="Show conversations" placement="right">
            <Button size="small" type="text" icon={<MenuUnfoldOutlined />} onClick={() => setSidebarCollapsed(false)} />
          </Tooltip>
          <Tooltip title={newChatTooltip} placement="right">
            <span className={cx(newChatDisabled && disabledTooltipTargetStyle)}>
              <Button
                size="small"
                type="text"
                icon={<PlusOutlined />}
                onClick={newConversation}
                disabled={newChatDisabled}
              />
            </span>
          </Tooltip>
        </div>
      ) : (
        <div
          className={css`
            width: 260px;
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            border-right: 1px solid ${colors.neutral[200]};
          `}
        >
          {/* Top row of the expanded sidebar: New chat (fills the row) + collapse toggle. */}
          <div
            className={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 12px;
            `}
          >
            <Tooltip title={isAtConversationLimit ? newChatTooltip : undefined}>
              <div
                className={cx(
                  css`
                    flex: 1;
                  `,
                  newChatDisabled && disabledTooltipTargetStyle,
                )}
              >
                <Button size="small" block onClick={newConversation} disabled={newChatDisabled}>
                  <PlusOutlined />
                  {' New chat'}
                </Button>
              </div>
            </Tooltip>
            <Tooltip title="Collapse sidebar">
              <Button size="small" type="text" icon={<MenuFoldOutlined />} onClick={() => setSidebarCollapsed(true)} />
            </Tooltip>
          </div>
          <div
            className={css`
              padding: 8px 16px 4px;
              font-size: 12px;
              font-weight: 600;
              letter-spacing: 0.05em;
              text-transform: uppercase;
              color: ${colors.neutral[400]};
            `}
          >
            Recent
          </div>
          <div
            className={css`
              flex: 1;
              min-height: 0;
              overflow-y: auto;
              padding: 4px 0 8px;
            `}
          >
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                className={cx(
                  css`
                    position: relative;
                    padding: 8px 52px 8px 16px;
                    cursor: pointer;

                    &:hover {
                      background: ${colors.neutral[100]};
                    }
                    &:hover .cwd-convo-actions {
                      opacity: 1;
                    }
                  `,
                  conversation.id === activeConversationId &&
                    css`
                      background: ${colors.neutral[200]};
                      box-shadow: inset 3px 0 0 ${colors.primary[600]};

                      &:hover {
                        background: ${colors.neutral[200]};
                      }
                    `,
                )}
                onClick={() => selectConversation(conversation.id)}
              >
                {editingId === conversation.id ? (
                  <Input
                    size="small"
                    autoFocus
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onPressEnter={() => commitRename(conversation.id)}
                    onBlur={() => commitRename(conversation.id)}
                  />
                ) : (
                  <>
                    <div
                      className={cx(
                        css`
                          font-size: 14px;
                          font-weight: 500;
                          color: ${colors.neutral[800]};
                          white-space: nowrap;
                          overflow: hidden;
                          text-overflow: ellipsis;
                        `,
                        'cwd-convo-title',
                      )}
                    >
                      {conversation.title}
                    </div>
                    <div
                      className={css`
                        font-size: 12px;
                        color: ${colors.neutral[400]};
                        margin-top: 2px;
                      `}
                    >
                      {formatConversationTimestamp(conversation.updatedAt)}
                    </div>
                    <div
                      className={cx(
                        css`
                          position: absolute;
                          top: 8px;
                          right: 8px;
                          display: flex;
                          gap: 8px;
                          color: ${colors.neutral[400]};
                          opacity: 0;
                          transition: opacity 0.15s ease;
                        `,
                        'cwd-convo-actions',
                      )}
                    >
                      <EditOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          startRename(conversation.id, conversation.title);
                        }}
                      />
                      <Popconfirm
                        title="Delete this conversation?"
                        okText="Delete"
                        cancelText="Cancel"
                        onConfirm={() => deleteConversation(conversation.id)}
                      >
                        <DeleteOutlined onClick={(e) => e.stopPropagation()} />
                      </Popconfirm>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <TokenUsageBox chatIsLoading={isLoading} />
        </div>
      )}

      <div
        className={css`
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        `}
      >
        <ChatConversation
          messages={messages}
          isLoading={isLoading}
          onSend={sendMessage}
          placeholder="Ask about an item, location, supplier, or customer..."
          header={<ChatPageHeader />}
          emptyState={<ChatStarters onSend={sendMessage} />}
        />
      </div>
    </div>
  );
}
